import React, { useMemo, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Utensils, Plus, Trash2, Search, Settings, Flame, TrendingUp, Activity,
  Beef, Wheat, Droplet, ChevronDown, ExternalLink, ArrowRight, Salad,
  Clock, Copy, BookmarkPlus, Bookmark, Wine, X, Lock, Pencil, Sparkles,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { useProfile } from '../context/ProfileContext';
import { useFeatureFlag } from '../context/FeatureFlagsContext';
import { useTierState } from '../hooks/useTierState';
import WidgetGate from '../components/WidgetGate';
import CustomFoodDialog from '../components/Nutrition/CustomFoodDialog';
import {
  FOODS, FOOD_CATEGORIES, NUTRITION_GOALS, ACTIVITY_LEVELS, MEAL_SLOTS,
  calcTargets, sumLog, inferNutritionGoal, SUPPLEMENT_PARTNERS,
  guessMealFromTime, groupEntriesByMeal, getRecentFoods, getYesterdayEntries,
} from '../data/nutrition';
import { localISO } from '../data/programs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------- Macro ring (pure SVG, no library) ----------
const Ring = ({ value, max, color, label, unit, big = false }) => {
  const pct = max > 0 ? Math.min(1, value / max) : 0;
  const R = big ? 56 : 40;
  const stroke = big ? 8 : 6;
  const C = 2 * Math.PI * R;
  const offset = C * (1 - pct);
  const size = (R + stroke) * 2;
  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={R} stroke="#27272a" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={R}
          stroke={color} strokeWidth={stroke} fill="none"
          strokeDasharray={C} strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.6s ease' }}
        />
      </svg>
      <div className="text-center mt-2">
        <div className={`font-display ${big ? 'text-3xl' : 'text-xl'} tracking-wide leading-none`}>
          {Math.round(value)}<span className="text-enf-text-muted text-xs ml-1">/{Math.round(max)}{unit}</span>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mt-1">{label}</div>
      </div>
    </div>
  );
};

// ---------- Inline setup form (when basic profile fields are missing) ----------
const NutritionSetup = ({ profile, update }) => {
  const [age, setAge] = useState(profile.age || '');
  const [sex, setSex] = useState(profile.sex || '');
  const [heightFt, setHeightFt] = useState(profile.heightFt || '');
  const [heightIn, setHeightIn] = useState(profile.heightIn || '');
  const [weight, setWeight] = useState(profile.weight || '');
  const valid = age && sex && heightFt && weight;
  const save = () => {
    if (!valid) return;
    update({
      age: Number(age),
      sex,
      heightFt: Number(heightFt),
      heightIn: Number(heightIn) || 0,
      weight: Number(weight),
    });
  };
  return (
    <div className="bg-transparent text-enf-text-primary min-h-screen pt-20 pb-16">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-enf-role-info text-xs uppercase tracking-[0.3em] font-semibold mb-2 flex items-center gap-2">
          <Salad className="w-3.5 h-3.5" /> Nutrition setup
        </div>
        <h1 className="font-display text-5xl md:text-6xl tracking-wide leading-none mb-3">Set your numbers.</h1>
        <p className="text-enf-text-secondary mb-8 max-w-xl">
          Calorie and macro targets need your age, sex, height, and weight. We use the Mifflin-St Jeor equation — most accurate predictor for active adults. Numbers stay on this device until you have an account.
        </p>

        <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Age</span>
              <input
                type="number" min="13" max="100" value={age}
                onChange={(e) => setAge(e.target.value)} onFocus={(e) => e.target.select()}
                className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
                data-testid="nutrition-setup-age"
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Weight (lb)</span>
              <input
                type="number" min="60" max="500" value={weight}
                onChange={(e) => setWeight(e.target.value)} onFocus={(e) => e.target.select()}
                className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
                data-testid="nutrition-setup-weight"
              />
            </label>
          </div>

          <div>
            <span className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Sex (for BMR calc)</span>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: 'male', label: 'Male' },
                { id: 'female', label: 'Female' },
              ].map((opt) => (
                <button key={opt.id} type="button" onClick={() => setSex(opt.id)}
                  className={`px-3 py-2.5 rounded-md text-sm font-semibold border transition-colors ${
                    sex === opt.id ? 'bg-enf-accent border-enf-accent-fg text-enf-text-primary' : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary hover:border-enf-surface-border'
                  }`}
                  data-testid={`nutrition-setup-sex-${opt.id}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <span className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Height</span>
            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <input
                  type="number" min="3" max="7" placeholder="ft" value={heightFt}
                  onChange={(e) => setHeightFt(e.target.value)} onFocus={(e) => e.target.select()}
                  className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
                  data-testid="nutrition-setup-height-ft"
                />
                <span className="text-[10px] uppercase tracking-widest text-enf-text-muted mt-1 block">Feet</span>
              </label>
              <label className="block">
                <input
                  type="number" min="0" max="11" placeholder="in" value={heightIn}
                  onChange={(e) => setHeightIn(e.target.value)} onFocus={(e) => e.target.select()}
                  className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
                  data-testid="nutrition-setup-height-in"
                />
                <span className="text-[10px] uppercase tracking-widest text-enf-text-muted mt-1 block">Inches</span>
              </label>
            </div>
          </div>

          <button
            onClick={save} disabled={!valid}
            className="w-full mt-2 py-3 bg-enf-accent hover:bg-enf-accent-hover disabled:bg-enf-surface-elevated disabled:text-enf-text-muted disabled:cursor-not-allowed rounded-md font-semibold text-sm flex items-center justify-center gap-2 transition-colors"
            data-testid="nutrition-setup-save"
          >
            Build my targets <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};


const NutritionPaywall = () => (
  <div className="bg-transparent text-enf-text-primary min-h-screen pt-20 pb-16">
    <div className="max-w-3xl mx-auto px-6">
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-enf-text-secondary hover:text-enf-text-primary text-sm mb-8 transition-colors">
        <ArrowRight className="w-4 h-4 rotate-180" /> Back to dashboard
      </Link>
      <div
        className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-8 md:p-12 text-center"
        data-testid="nutrition-paywall"
      >
        <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-enf-accent/10 border border-enf-accent-fg/30 flex items-center justify-center">
          <Lock className="w-6 h-6 text-enf-accent-fg" />
        </div>
        <div className="text-xs uppercase tracking-[0.3em] font-semibold text-enf-accent-fg mb-2">Enferno feature</div>
        <h1 className="font-display text-3xl md:text-4xl tracking-wide leading-tight mb-3">Unlock Nutrition</h1>
        <p className="text-enf-text-secondary text-sm leading-relaxed max-w-md mx-auto mb-6">
          Macros, calorie targets, the Food Library (450+ items), meal logging, and the supplement partners catalog are all part of Enferno.
        </p>
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold transition-colors"
          data-testid="nutrition-paywall-cta"
        >
          See pricing <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  </div>
);

const NutritionApp = () => {
  const { profile, update } = useProfile();
  const { enabled: affiliatesEnabled } = useFeatureFlag('affiliates');
  const todayIso = localISO(new Date());

  // Settings + state
  const goalId = profile.nutritionGoal || inferNutritionGoal(profile.goals || []);
  const activityId = profile.activityLevel || 'moderate';
  const targets = useMemo(() => calcTargets({ profile, goalId, activityId }), [profile, goalId, activityId]);
  const todayLog = useMemo(
    () => (profile.foodLog && profile.foodLog[todayIso]) || [],
    [profile.foodLog, todayIso]
  );
  const totals = useMemo(() => sumLog(todayLog), [todayLog]);

  // Add-food dialog
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState('');
  const [activeCat, setActiveCat] = useState('all');
  const [pickedFood, setPickedFood] = useState(null);
  const [servings, setServings] = useState(1);
  // Default meal slot in the add dialog — guess from current time. Resets each
  // time the dialog opens. User can override per entry.
  const [pendingMeal, setPendingMeal] = useState(() => guessMealFromTime());
  // Picker mode tabs: 'all' (categories), 'recent', 'saved'
  const [pickerMode, setPickerMode] = useState('all');

  // Settings dialog
  const [showSettings, setShowSettings] = useState(false);

  // Save-as-meal dialog
  const [savingMealName, setSavingMealName] = useState('');
  const [showSaveMeal, setShowSaveMeal] = useState(false);

  // Toast for undoable actions (copy yesterday)
  const [toast, setToast] = useState(null); // { msg, onUndo }

  // ---- Custom foods (G11 — paid feature, picker-only entry) ----
  const { is_paid: isPaid } = useTierState();
  const [customFoods, setCustomFoods] = useState([]);
  const [customFoodDialog, setCustomFoodDialog] = useState(null);  // null | { editing?: Food }

  const refreshCustomFoods = useCallback(async () => {
    if (!isPaid) { setCustomFoods([]); return; }
    try {
      const res = await axios.get(`${API}/nutrition/custom-foods`);
      setCustomFoods(res.data?.foods || []);
    } catch (e) {
      // Non-fatal: picker just won't show custom foods this session.
      console.warn('Custom foods fetch failed', e?.message);
    }
  }, [isPaid]);

  useEffect(() => { refreshCustomFoods(); }, [refreshCustomFoods]);

  // Recent foods derived from full foodLog history.
  const recentFoods = useMemo(() => getRecentFoods(profile.foodLog || {}, 12), [profile.foodLog]);

  // Alcohol gate — hide alcohol items from filter results unless user opted in.
  // Custom foods merge in here so the picker's search + category filter "just
  // work" without separate branches downstream. They're tagged with `isCustom`
  // so the row can render the edit affordance.
  const visibleFoods = useMemo(() => {
    const base = profile.showAlcohol ? FOODS : FOODS.filter((f) => !f.alcohol);
    const customMapped = customFoods.map((f) => ({
      ...f,
      cat: f.category || 'mixed',  // category → cat alias so the existing filter works
    }));
    return [...base, ...customMapped];
  }, [profile.showAlcohol, customFoods]);

  // Categories filtered down too (no Alcohol tab if disabled)
  const visibleCategories = useMemo(() => {
    // Inject "My foods" pill at the front when the user has any custom
    // foods. Discoverability fix per founder — without this the
    // user could create a custom food and have no clear path to see/find
    // it again.
    const baseCats = profile.showAlcohol ? FOOD_CATEGORIES : FOOD_CATEGORIES.filter((c) => c.id !== 'alcohol');
    if (customFoods.length > 0) {
      return [
        { id: 'mine', label: `Custom Foods (${customFoods.length})` },
        ...baseCats,
      ];
    }
    return baseCats;
  }, [profile.showAlcohol, customFoods]);

  const filteredFoods = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = visibleFoods.filter((f) => {
      // "My foods" pseudo-category filters to user's customs only.
      if (activeCat === 'mine') {
        if (!f.isCustom) return false;
      } else if (activeCat !== 'all' && f.cat !== activeCat) {
        return false;
      }
      if (q && !f.name.toLowerCase().includes(q)) return false;
      return true;
    });
    if (!q) {
      // No search → straight alphabetical by name
      return [...base].sort((a, b) => a.name.localeCompare(b.name));
    }
    // Search active → relevance ranking:
    //   0 = name starts with query
    //   1 = any word inside name starts with query (e.g., "Greek Yogurt" vs "yog")
    //   2 = query appears anywhere else in name
    // Items tied on rank are alphabetized.
    const rank = (name) => {
      const lower = name.toLowerCase();
      if (lower.startsWith(q)) return 0;
      const words = lower.split(/[\s(/-]+/);
      if (words.some((w) => w.startsWith(q))) return 1;
      return 2;
    };
    return [...base].sort((a, b) => {
      const ra = rank(a.name);
      const rb = rank(b.name);
      if (ra !== rb) return ra - rb;
      return a.name.localeCompare(b.name);
    });
  }, [search, activeCat, visibleFoods]);

  const openAdd = (meal) => {
    setPendingMeal(meal || guessMealFromTime());
    setPickerMode('all');
    setSearch('');
    setActiveCat('all');
    setPickedFood(null);
    setServings(1);
    setShowAdd(true);
  };

  const addFood = () => {
    if (!pickedFood) return;
    // Custom foods snapshot their macros onto the log entry at log time.
    // Per founder: editing or deleting the custom food never
    // retroactively changes prior log entries. Library foods stay reference-
    // only (FOODS array is the canonical source).
    const isCustomEntry = !!pickedFood.isCustom;
    const entry = {
      id: `${Date.now()}-${pickedFood.id}`,
      foodId: pickedFood.id,
      servings: Number(servings) || 1,
      meal: pendingMeal,
      time: new Date().toISOString(),
      ...(isCustomEntry && {
        foodSnapshot: {
          id: pickedFood.id,
          name: pickedFood.name,
          kcal: pickedFood.kcal,
          protein: pickedFood.protein,
          carbs: pickedFood.carbs,
          fat: pickedFood.fat,
          defaultServing: pickedFood.defaultServing,
          isCustom: true,
        },
      }),
    };
    const nextLog = {
      ...(profile.foodLog || {}),
      [todayIso]: [...todayLog, entry],
    };
    update({ foodLog: nextLog });
    setShowAdd(false);
    setPickedFood(null);
    setServings(1);
    setSearch('');
  };

  const removeEntry = (entryId) => {
    const nextLog = {
      ...(profile.foodLog || {}),
      [todayIso]: todayLog.filter((e) => e.id !== entryId),
    };
    update({ foodLog: nextLog });
  };

  // ----- Copy yesterday -----
  const copyYesterday = () => {
    const ye = getYesterdayEntries(profile.foodLog || {}, todayIso);
    if (ye.length === 0) {
      setToast({ msg: 'Nothing logged yesterday to copy.', onUndo: null });
      setTimeout(() => setToast(null), 3000);
      return;
    }
    const stamped = ye.map((e, i) => ({
      id: `${Date.now()}-${i}-${e.foodId}`,
      foodId: e.foodId,
      servings: e.servings,
      meal: e.meal,
      time: new Date().toISOString(),
    }));
    const prevToday = todayLog.slice();
    const nextLog = {
      ...(profile.foodLog || {}),
      [todayIso]: [...todayLog, ...stamped],
    };
    update({ foodLog: nextLog });
    setToast({
      msg: `Copied ${stamped.length} ${stamped.length === 1 ? 'entry' : 'entries'} from yesterday.`,
      onUndo: () => {
        update({ foodLog: { ...(profile.foodLog || {}), [todayIso]: prevToday } });
        setToast(null);
      },
    });
    setTimeout(() => setToast(null), 5000);
  };

  // ----- Saved meals -----
  const savedMeals = profile.savedMeals || [];

  const saveCurrentAsMeal = () => {
    const name = (savingMealName || '').trim();
    if (!name || todayLog.length === 0) return;
    const meal = {
      id: `saved-${Date.now()}`,
      name,
      // Strip id/time, keep foodId/servings/meal slot
      entries: todayLog.map((e) => ({
        foodId: e.foodId,
        servings: e.servings,
        meal: e.meal || 'uncategorized',
      })),
      createdAt: new Date().toISOString(),
    };
    update({ savedMeals: [meal, ...savedMeals] });
    setSavingMealName('');
    setShowSaveMeal(false);
    setToast({ msg: `Saved "${name}" — load it from the Saved tab next time.`, onUndo: null });
    setTimeout(() => setToast(null), 4000);
  };

  const loadSavedMeal = (saved) => {
    const stamped = saved.entries.map((e, i) => ({
      id: `${Date.now()}-${i}-${e.foodId}`,
      foodId: e.foodId,
      servings: e.servings,
      meal: e.meal || pendingMeal,
      time: new Date().toISOString(),
    }));
    const prevToday = todayLog.slice();
    const nextLog = {
      ...(profile.foodLog || {}),
      [todayIso]: [...todayLog, ...stamped],
    };
    update({ foodLog: nextLog });
    setShowAdd(false);
    setToast({
      msg: `Loaded "${saved.name}" — ${stamped.length} ${stamped.length === 1 ? 'entry' : 'entries'} added.`,
      onUndo: () => {
        update({ foodLog: { ...(profile.foodLog || {}), [todayIso]: prevToday } });
        setToast(null);
      },
    });
    setTimeout(() => setToast(null), 5000);
  };

  const deleteSavedMeal = (id) => {
    update({ savedMeals: savedMeals.filter((m) => m.id !== id) });
  };

  // Pre-grouped entries for the log section
  const grouped = useMemo(() => groupEntriesByMeal(todayLog), [todayLog]);

  const setGoal = (id) => update({ nutritionGoal: id });
  const setActivity = (id) => update({ activityLevel: id });

  const calsRemaining = Math.max(0, targets.calories - totals.kcal);
  const calsOver = Math.max(0, totals.kcal - targets.calories);

  // No profile basics → render inline setup form (no bounce to onboarding)
  if (!profile.weight || !profile.age || !profile.sex || !profile.heightFt) {
    return <NutritionSetup profile={profile} update={update} />;
  }

  return (
    <div className="bg-transparent text-enf-text-primary min-h-screen pt-20 pb-16">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-enf-role-info text-xs uppercase tracking-[0.3em] font-semibold mb-2 flex items-center gap-2">
              <Utensils className="w-3.5 h-3.5" /> Nutrition · {new Date(todayIso + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
            </div>
            <h1 className="font-display text-6xl md:text-7xl tracking-wide leading-none">Fuel the heat.</h1>
            <p className="text-enf-text-secondary mt-3 max-w-xl">
              Daily targets tuned to your goal, activity level, and body. Log what you eat — Enferno does the math.
            </p>
          </div>
          <button onClick={() => setShowSettings(true)} className="px-4 py-2 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm flex items-center gap-2 transition-colors" data-testid="nutrition-settings-btn">
            <Settings className="w-4 h-4" /> Goal & activity
          </button>
        </div>

        {/* Targets summary + rings */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
          {/* Cal ring big card */}
          <div className="md:col-span-1 bg-enf-surface-deep border border-enf-border-special/30 rounded-xl p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest text-enf-role-info font-semibold flex items-center gap-2">
                <Flame className="w-3.5 h-3.5 scale-x-[-1]" /> Daily target
              </div>
              <div className="text-[10px] uppercase tracking-widest text-enf-text-muted font-bold">
                {targets.goal.name} · {targets.activity.name}
              </div>
            </div>
            <div className="flex items-center justify-around">
              <Ring value={totals.kcal} max={targets.calories} color="#dc2626" label="kcal" unit="" big />
            </div>
            <div className="mt-5 text-center">
              {calsOver > 0 ? (
                <div className="text-enf-role-warning font-semibold">+{Math.round(calsOver)} over target</div>
              ) : (
                <div className="text-enf-text-secondary"><span className="font-bold">{Math.round(calsRemaining)}</span> <span className="text-enf-text-muted text-sm">kcal remaining</span></div>
              )}
              <div className="text-xs text-enf-text-muted mt-1">
                BMR {targets.bmr} · TDEE {targets.tdee} · Adjust {targets.goal.adjust >= 0 ? '+' : ''}{targets.goal.adjust}
              </div>
            </div>
          </div>

          {/* Macro rings */}
          <div className="md:col-span-2 bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6">
            <div className="text-xs uppercase tracking-widest text-enf-text-secondary font-semibold mb-5">Macros</div>
            <div className="grid grid-cols-3 gap-4">
              <Ring value={totals.protein} max={targets.proteinG} color="#dc2626" label="Protein" unit="g" />
              <Ring value={totals.carbs} max={targets.carbG} color="#7691b1" label="Carbs" unit="g" />
              <Ring value={totals.fat} max={targets.fatG} color="#f59e0b" label="Fat" unit="g" />
            </div>
            <div className="grid grid-cols-3 gap-4 mt-5 text-center text-xs">
              <div className="text-enf-text-muted">{Math.round(targets.proteinG * 4)} kcal target</div>
              <div className="text-enf-text-muted">{Math.round(targets.carbG * 4)} kcal target</div>
              <div className="text-enf-text-muted">{Math.round(targets.fatG * 9)} kcal target</div>
            </div>
          </div>
        </div>

        {/* Food log — grouped by meal slot */}
        <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6 mb-8">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div>
              <div className="text-sm font-semibold uppercase tracking-widest text-enf-text-secondary">Today's food log</div>
              <div className="text-xs text-enf-text-muted mt-1">{todayLog.length} {todayLog.length === 1 ? 'entry' : 'entries'} · {Math.round(totals.kcal)} kcal logged</div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={copyYesterday}
                className="px-3 py-2 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-xs font-semibold flex items-center gap-1.5 transition-colors"
                title="Copy yesterday's entries into today"
                data-testid="nutrition-copy-yesterday"
              >
                <Copy className="w-3.5 h-3.5" /> Copy yesterday
              </button>
              {todayLog.length > 0 && (
                <button
                  onClick={() => setShowSaveMeal(true)}
                  className="px-3 py-2 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-xs font-semibold flex items-center gap-1.5 transition-colors"
                  title="Save today's log as a reusable meal template"
                  data-testid="nutrition-save-meal"
                >
                  <BookmarkPlus className="w-3.5 h-3.5" /> Save as meal
                </button>
              )}
              <button onClick={() => openAdd()} className="px-4 py-2.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md font-semibold text-sm flex items-center gap-2 transition-colors" data-testid="nutrition-add-food-btn">
                <Plus className="w-4 h-4" /> Log food
              </button>
            </div>
          </div>

          {todayLog.length === 0 ? (
            <div className="text-center py-10 text-enf-text-muted">
              <Utensils className="w-6 h-6 mx-auto mb-2 opacity-50" />
              <div className="text-sm">No food logged today. Tap <span className="text-enf-accent-fg font-semibold">Log food</span> to start.</div>
            </div>
          ) : (
            <div className="space-y-4">
              {[...MEAL_SLOTS, { id: 'uncategorized', label: 'Logged' }].map((slot) => {
                const entries = grouped[slot.id] || [];
                if (entries.length === 0) return null;
                const slotTotals = sumLog(entries);
                return (
                  <div key={slot.id} data-testid={`meal-slot-${slot.id}`}>
                    <div className="flex items-center justify-between mb-2 px-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] uppercase tracking-[0.2em] text-enf-role-info font-semibold">{slot.label}</span>
                        <button
                          onClick={() => openAdd(slot.id === 'uncategorized' ? 'snack' : slot.id)}
                          className="text-enf-text-muted hover:text-enf-accent-fg p-1 transition-colors"
                          title={`Add to ${slot.label}`}
                          data-testid={`meal-slot-add-${slot.id}`}
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <span className="text-[10px] text-enf-text-muted">{Math.round(slotTotals.kcal)} kcal · P {Math.round(slotTotals.protein)} / C {Math.round(slotTotals.carbs)} / F {Math.round(slotTotals.fat)}</span>
                    </div>
                    <div className="space-y-1.5">
                      {entries.map((e) => {
                        const food = e.foodSnapshot || FOODS.find((f) => f.id === e.foodId) || customFoods.find((f) => f.id === e.foodId);
                        if (!food) return null;
                        const s = Number(e.servings) || 1;
                        return (
                          <div key={e.id} className="flex items-center gap-3 p-3 bg-enf-surface-mid border border-enf-surface-border rounded-md group" data-testid={`food-log-entry-${e.id}`}>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold text-enf-text-primary truncate">{food.name}</div>
                              <div className="text-xs text-enf-text-muted">{s}× {food.defaultServing.label}</div>
                            </div>
                            <div className="text-right text-sm hidden sm:flex flex-shrink-0 items-center gap-3">
                              <span><span className="font-bold">{Math.round(food.kcal * s)}</span><span className="text-enf-text-muted text-xs"> kcal</span></span>
                              <span className="text-enf-accent-fg text-xs">P {Math.round(food.protein * s)}</span>
                              <span className="text-enf-role-info text-xs">C {Math.round(food.carbs * s)}</span>
                              <span className="text-amber-500 text-xs">F {Math.round(food.fat * s)}</span>
                            </div>
                            <div className="sm:hidden text-right text-xs flex-shrink-0">
                              <div className="font-bold">{Math.round(food.kcal * s)} <span className="text-enf-text-muted">kcal</span></div>
                              <div className="text-enf-text-muted">P {Math.round(food.protein * s)}/C {Math.round(food.carbs * s)}/F {Math.round(food.fat * s)}</div>
                            </div>
                            <button onClick={() => removeEntry(e.id)} className="text-enf-text-muted hover:text-enf-role-warning p-1 transition-colors" data-testid={`food-log-remove-${e.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Supplement affiliate strip — double-gated: admin flag + user widget pref */}
        {affiliatesEnabled && (
        <WidgetGate page="nutrition" id="supplementAffiliates" className="mb-8">
          <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold uppercase tracking-widest text-enf-text-secondary flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-enf-accent-fg" /> Supplement partners
            </div>
            <span className="text-[10px] uppercase tracking-widest text-enf-role-info font-bold">Affiliate slots</span>
          </div>
          <p className="text-xs text-enf-text-muted mb-5 leading-relaxed">
            Curated picks from upcoming partner brands. Enferno earns a small commission on purchases — proceeds support the IronLimb mission. Links currently placeholder until partnerships are signed.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {SUPPLEMENT_PARTNERS.map((p) => (
              <a key={p.id} href={p.url} target="_blank" rel="noopener noreferrer" className="bg-enf-surface-mid border border-enf-surface-border rounded-lg p-4 hover:border-enf-surface-border transition-colors group" data-testid={`supplement-card-${p.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] uppercase tracking-widest text-enf-role-info font-bold">{p.tag}</span>
                  <ExternalLink className="w-3 h-3 text-enf-text-muted group-hover:text-enf-role-info transition-colors" />
                </div>
                <div className="font-display text-lg tracking-wide leading-tight mb-1">{p.name}</div>
                <div className="text-xs text-enf-text-muted mb-2">{p.priceRange}</div>
                <p className="text-xs text-enf-text-secondary leading-relaxed">{p.why}</p>
              </a>
            ))}
          </div>
          </div>
        </WidgetGate>
        )}
      </div>

      {/* Add food dialog */}
      <Dialog open={showAdd} onOpenChange={(o) => { setShowAdd(o); if (!o) { setPickedFood(null); setSearch(''); } }}>
        <DialogContent className="bg-enf-surface-deep border-enf-surface-border text-enf-text-primary max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl tracking-wide leading-none">Log food</DialogTitle>
            <DialogDescription className="text-enf-text-secondary">
              Pick from the curated database. More items + scan-by-barcode coming with cloud backend.
            </DialogDescription>
          </DialogHeader>

          {!pickedFood ? (
            <>
              {/* Meal slot picker — always visible above the food picker */}
              <div className="flex flex-wrap gap-1.5 mt-2 mb-3">
                <span className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold flex items-center mr-1">For:</span>
                {MEAL_SLOTS.map((m) => (
                  <button key={m.id} onClick={() => setPendingMeal(m.id)}
                    className={`px-2.5 py-1 text-xs rounded border transition-colors ${
                      pendingMeal === m.id ? 'bg-enf-role-info/20 border-enf-role-info text-enf-role-info' : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary hover:border-enf-surface-border'
                    }`}
                    data-testid={`meal-pick-${m.id}`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>

              {/* Source tabs: All foods · Recent · Saved meals */}
              <div className="flex gap-1 mb-2 border-b border-enf-surface-border">
                {[
                  { id: 'all', label: 'All foods' },
                  { id: 'recent', label: `Recent${recentFoods.length ? ` (${recentFoods.length})` : ''}` },
                  { id: 'saved', label: `Saved meals${savedMeals.length ? ` (${savedMeals.length})` : ''}` },
                ].map((tab) => (
                  <button key={tab.id} onClick={() => setPickerMode(tab.id)}
                    className={`px-3 py-1.5 text-xs font-semibold transition-colors -mb-px ${
                      pickerMode === tab.id ? 'text-enf-accent-fg border-b-2 border-enf-accent-fg' : 'text-enf-text-secondary hover:text-enf-text-primary'
                    }`}
                    data-testid={`picker-tab-${tab.id}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {pickerMode === 'all' && (
                <>
                  <div className="relative mt-2">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-enf-text-muted" />
                    <input
                      type="text"
                      placeholder="Search foods..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="w-full pl-9 pr-3 py-2.5 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg"
                      data-testid="food-search-input"
                      autoFocus
                    />
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {visibleCategories.map((c) => (
                      <button key={c.id} onClick={() => setActiveCat(c.id)}
                        className={`px-2.5 py-1 text-xs rounded border transition-colors ${
                          activeCat === c.id ? 'bg-enf-accent border-enf-accent-fg text-enf-text-primary' : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary hover:border-enf-surface-border'
                        }`}>
                        {c.label}
                      </button>
                    ))}
                  </div>
                  {isPaid && (
                    <button
                      type="button"
                      onClick={() => {
                        // Temporarily close the Log Food dialog so its
                        // Radix focus trap releases — otherwise the
                        // portaled Custom Food dialog can render on top
                        // visually but its inputs can't receive focus
                        // (per founder). Restored on Custom
                        // Food close.
                        setShowAdd(false);
                        setCustomFoodDialog({});
                      }}
                      data-testid="custom-food-add-btn"
                      className="w-full mt-3 px-3 py-2 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-dashed border-enf-accent-fg/40 hover:border-enf-accent-fg rounded text-sm text-enf-accent-fg flex items-center justify-center gap-2 transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" /> Custom food
                    </button>
                  )}
                  <div className="mt-3 max-h-[55vh] overflow-y-auto space-y-1.5 pr-1">
                    {filteredFoods.length === 0 && (
                      <div className="text-center py-8 text-enf-text-muted text-sm">No foods match. Try a different category or clear the search.</div>
                    )}
                    {filteredFoods.map((f) => (
                      <div key={f.id} className="flex items-stretch gap-1">
                        <button onClick={() => setPickedFood(f)}
                          className="flex-1 text-left p-3 bg-enf-surface-mid border border-enf-surface-border hover:border-enf-accent-fg rounded-md transition-colors flex items-center justify-between gap-3"
                          data-testid={`food-pick-${f.id}`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-enf-text-primary truncate flex items-center gap-2">
                              {f.name}
                              {f.isCustom && (
                                <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-widest bg-enf-accent-fg/15 border border-enf-accent-fg/40 text-enf-accent-fg rounded">Custom</span>
                              )}
                            </div>
                            <div className="text-xs text-enf-text-muted">{f.defaultServing.label} · {f.kcal} kcal · P {f.protein} / C {f.carbs} / F {f.fat}</div>
                          </div>
                          <Plus className="w-4 h-4 text-enf-text-muted flex-shrink-0" />
                        </button>
                        {f.isCustom && (
                          <button
                            type="button"
                            onClick={(ev) => {
                              ev.stopPropagation();
                              // Same dance as the add button — release
                              // the Log Food focus trap before opening
                              // the edit dialog.
                              setShowAdd(false);
                              setCustomFoodDialog({ editing: f });
                            }}
                            className="px-2 bg-enf-surface-mid border border-enf-surface-border hover:border-enf-accent-fg rounded-md text-enf-text-muted hover:text-enf-text-primary transition-colors"
                            data-testid={`custom-food-edit-${f.id}`}
                            title="Edit this custom food"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {pickerMode === 'recent' && (
                <div className="mt-2 max-h-[55vh] overflow-y-auto space-y-1.5 pr-1">
                  {recentFoods.length === 0 ? (
                    <div className="text-center py-10 text-enf-text-muted text-sm">
                      <Clock className="w-5 h-5 mx-auto mb-2 opacity-50" />
                      No recent foods yet. Log a few items and they'll show up here.
                    </div>
                  ) : recentFoods.map((f) => (
                    <button key={f.id} onClick={() => setPickedFood(f)}
                      className="w-full text-left p-3 bg-enf-surface-mid border border-enf-surface-border hover:border-enf-accent-fg rounded-md transition-colors flex items-center justify-between gap-3"
                      data-testid={`food-pick-recent-${f.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-enf-text-primary truncate">{f.name}</div>
                        <div className="text-xs text-enf-text-muted">{f.defaultServing.label} · {f.kcal} kcal · P {f.protein} / C {f.carbs} / F {f.fat}</div>
                      </div>
                      <Plus className="w-4 h-4 text-enf-text-muted flex-shrink-0" />
                    </button>
                  ))}
                </div>
              )}

              {pickerMode === 'saved' && (
                <div className="mt-2 max-h-[55vh] overflow-y-auto space-y-1.5 pr-1">
                  {savedMeals.length === 0 ? (
                    <div className="text-center py-10 text-enf-text-muted text-sm">
                      <Bookmark className="w-5 h-5 mx-auto mb-2 opacity-50" />
                      No saved meals yet. Build a day's log and tap <span className="text-enf-accent-fg font-semibold">Save as meal</span> to keep it.
                    </div>
                  ) : savedMeals.map((m) => {
                    const mTotals = sumLog(m.entries);
                    return (
                      <div key={m.id} className="flex items-center gap-2 p-3 bg-enf-surface-mid border border-enf-surface-border rounded-md group" data-testid={`saved-meal-${m.id}`}>
                        <button onClick={() => loadSavedMeal(m)} className="flex-1 min-w-0 text-left">
                          <div className="text-sm font-semibold text-enf-text-primary truncate">{m.name}</div>
                          <div className="text-xs text-enf-text-muted">{m.entries.length} items · {Math.round(mTotals.kcal)} kcal · P {Math.round(mTotals.protein)} / C {Math.round(mTotals.carbs)} / F {Math.round(mTotals.fat)}</div>
                        </button>
                        <button onClick={() => loadSavedMeal(m)} className="px-3 py-1.5 bg-enf-accent hover:bg-enf-accent-hover rounded text-xs font-semibold" data-testid={`saved-meal-load-${m.id}`}>
                          Load
                        </button>
                        <button onClick={() => deleteSavedMeal(m.id)} className="text-enf-text-muted hover:text-enf-role-warning p-1" title="Delete saved meal" data-testid={`saved-meal-delete-${m.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <div className="mt-2">
              <button onClick={() => setPickedFood(null)} className="text-xs text-enf-role-info hover:text-enf-text-primary mb-3">← Pick a different food</button>
              <div className="p-4 bg-enf-surface-mid border border-enf-surface-border rounded-md mb-4">
                <div className="font-display text-2xl tracking-wide leading-none mb-1">{pickedFood.name}</div>
                <div className="text-xs text-enf-text-muted mb-3">Per {pickedFood.defaultServing.label}</div>
                <div className="grid grid-cols-4 gap-3 text-center">
                  <div><div className="font-bold">{pickedFood.kcal}</div><div className="text-[10px] text-enf-text-muted uppercase tracking-widest">kcal</div></div>
                  <div><div className="font-bold text-enf-accent-fg">{pickedFood.protein}g</div><div className="text-[10px] text-enf-text-muted uppercase tracking-widest">Protein</div></div>
                  <div><div className="font-bold text-enf-role-info">{pickedFood.carbs}g</div><div className="text-[10px] text-enf-text-muted uppercase tracking-widest">Carbs</div></div>
                  <div><div className="font-bold text-amber-500">{pickedFood.fat}g</div><div className="text-[10px] text-enf-text-muted uppercase tracking-widest">Fat</div></div>
                </div>
              </div>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Servings</span>
                <input
                  type="number"
                  step="0.25"
                  min="0.25"
                  value={servings}
                  onChange={(e) => setServings(e.target.value)}
                  onFocus={(e) => e.target.select()}
                  className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary text-lg focus:outline-none focus:border-enf-accent-fg"
                  data-testid="food-servings-input"
                />
              </label>
              <div className="mt-3 p-3 bg-enf-surface-special/30 border border-enf-border-special/30 rounded-md text-sm">
                Logging: <span className="font-bold">{Math.round(pickedFood.kcal * (Number(servings) || 1))} kcal</span>
                <span className="text-enf-text-secondary"> · P {Math.round(pickedFood.protein * (Number(servings) || 1))}g / C {Math.round(pickedFood.carbs * (Number(servings) || 1))}g / F {Math.round(pickedFood.fat * (Number(servings) || 1))}g</span>
              </div>
              <button onClick={addFood} className="w-full mt-4 py-2.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold transition-colors flex items-center justify-center gap-2" data-testid="food-confirm-add">
                <Plus className="w-4 h-4" /> Add to today
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Settings dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="bg-enf-surface-deep border-enf-surface-border text-enf-text-primary max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl tracking-wide leading-none">Nutrition goal</DialogTitle>
            <DialogDescription className="text-enf-text-secondary">
              Pick the nutrition goal and activity level that match your current chapter. Numbers recompute instantly.
            </DialogDescription>
          </DialogHeader>

          <div className="text-xs uppercase tracking-widest text-enf-role-info font-semibold mt-2 mb-2">Goal</div>
          <div className="grid sm:grid-cols-2 gap-2 mb-5">
            {NUTRITION_GOALS.map((g) => {
              const active = goalId === g.id;
              return (
                <button key={g.id} onClick={() => setGoal(g.id)}
                  className={`text-left p-3 rounded-md border transition-colors ${
                    active ? 'bg-enf-accent/10 border-enf-accent-fg' : 'bg-enf-surface-mid border-enf-surface-border hover:border-enf-surface-border'
                  }`}
                  data-testid={`nutrition-goal-${g.id}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-bold">{g.name}</div>
                    <div className="text-xs text-enf-text-muted">{g.adjust >= 0 ? '+' : ''}{g.adjust} kcal</div>
                  </div>
                  <div className="text-xs text-enf-text-muted">{g.detail}</div>
                </button>
              );
            })}
          </div>

          <div className="text-xs uppercase tracking-widest text-enf-role-info font-semibold mb-2">Activity level</div>
          <div className="space-y-2">
            {ACTIVITY_LEVELS.map((a) => {
              const active = activityId === a.id;
              return (
                <button key={a.id} onClick={() => setActivity(a.id)}
                  className={`w-full text-left p-3 rounded-md border transition-colors flex items-center justify-between gap-3 ${
                    active ? 'bg-enf-accent/10 border-enf-accent-fg' : 'bg-enf-surface-mid border-enf-surface-border hover:border-enf-surface-border'
                  }`}
                  data-testid={`activity-level-${a.id}`}
                >
                  <div>
                    <div className="font-bold text-sm">{a.name}</div>
                    <div className="text-xs text-enf-text-muted">{a.detail}</div>
                  </div>
                  <div className="text-xs text-enf-text-muted font-mono">×{a.multiplier}</div>
                </button>
              );
            })}
          </div>

          {/* Alcohol gate — opt in to show alcoholic beverages in the picker */}
          <div className="text-xs uppercase tracking-widest text-enf-role-info font-semibold mt-6 mb-2">Picker visibility</div>
          <label className="flex items-center gap-3 p-3 bg-enf-surface-mid border border-enf-surface-border rounded-md cursor-pointer hover:border-enf-surface-border transition-colors">
            <input
              type="checkbox"
              checked={!!profile.showAlcohol}
              onChange={(e) => update({ showAlcohol: e.target.checked })}
              className="w-4 h-4 accent-enf-accent"
              data-testid="nutrition-show-alcohol-toggle"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 font-semibold text-sm"><Wine className="w-4 h-4 text-enf-text-secondary" /> Show alcohol in food picker</div>
              <div className="text-xs text-enf-text-muted mt-0.5">Beer, wine, cocktails, and hard liquor. Off by default.</div>
            </div>
          </label>
        </DialogContent>
      </Dialog>

      {/* Save-as-meal dialog */}
      <Dialog open={showSaveMeal} onOpenChange={setShowSaveMeal}>
        <DialogContent className="bg-enf-surface-deep border-enf-surface-border text-enf-text-primary max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl tracking-wide leading-none">Save today's log as a meal</DialogTitle>
            <DialogDescription className="text-enf-text-secondary">
              Name it something you'll recognize — "Post-workout shake," "Usual breakfast," "Sunday roast." Load it with one tap next time.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            <label className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Meal name</label>
            <input
              type="text"
              value={savingMealName}
              onChange={(e) => setSavingMealName(e.target.value)}
              placeholder="Usual breakfast"
              className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
              autoFocus
              data-testid="save-meal-name-input"
            />
            <div className="mt-3 text-xs text-enf-text-muted">
              Includes {todayLog.length} {todayLog.length === 1 ? 'entry' : 'entries'} · {Math.round(totals.kcal)} kcal
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={() => setShowSaveMeal(false)} className="flex-1 py-2.5 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm font-semibold">Cancel</button>
            <button onClick={saveCurrentAsMeal} disabled={!savingMealName.trim()} className="flex-1 py-2.5 bg-enf-accent hover:bg-enf-accent-hover disabled:bg-enf-surface-elevated disabled:text-enf-text-muted disabled:cursor-not-allowed rounded-md text-sm font-semibold flex items-center justify-center gap-2" data-testid="save-meal-confirm">
              <Bookmark className="w-4 h-4" /> Save meal
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Toast (copy yesterday / load saved meal undo) */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 max-w-md w-full px-4">
          <div className="bg-enf-surface-deep border border-enf-role-info/40 rounded-md p-3 flex items-center gap-3 shadow-xl" data-testid="nutrition-toast">
            <div className="flex-1 text-sm">{toast.msg}</div>
            {toast.onUndo && (
              <button onClick={toast.onUndo} className="px-3 py-1.5 bg-enf-role-info/20 hover:bg-enf-role-info/30 border border-enf-role-info/40 text-enf-role-info rounded text-xs font-semibold" data-testid="nutrition-toast-undo">
                Undo
              </button>
            )}
            <button onClick={() => setToast(null)} className="text-enf-text-muted hover:text-enf-text-primary p-1">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* Custom Food dialog — G11. Picker-only entry; opens on top
          of the Log-food dialog via React portal. We close Log Food before
          this opens (focus-trap conflict) and re-open it on close. */}
      {customFoodDialog && (
        <CustomFoodDialog
          editing={customFoodDialog.editing}
          onClose={() => {
            setCustomFoodDialog(null);
            setShowAdd(true);  // restore Log Food dialog
          }}
          onSaved={(saved) => {
            refreshCustomFoods();
            setCustomFoodDialog(null);
            setShowAdd(true);  // restore Log Food dialog
            // If we just created a new food, auto-pick it so the user can
            // log it without re-searching.
            if (saved && !customFoodDialog.editing) setPickedFood(saved);
          }}
          onDeleted={() => {
            refreshCustomFoods();
            setCustomFoodDialog(null);
            setShowAdd(true);  // restore Log Food dialog
          }}
        />
      )}
    </div>
  );
};

// G5: Public Nutrition export — gates entry on tier state. Free users (Egnite)
// see the paywall; paid users get the full NutritionApp.
const Nutrition = () => {
  const { is_paid: isPaid, loading: tierLoading } = useTierState();
  if (tierLoading) return null;
  if (!isPaid) return <NutritionPaywall />;
  return <NutritionApp />;
};

export default Nutrition;
